// @ts-check
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { tolerantId } from "./firestoreHelpers";
import { consultaDelCierre } from "./consultasEdificio";

/** @typedef {{ ref: import("firebase/firestore").DocumentReference, data: Record<string, any> }} BatchUpdate */

const BATCH_MAX = 450;

/**
 * Ejecuta updates en lotes de hasta 450 operaciones por batch de Firestore.
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {BatchUpdate[]} updates - Lista de refs y datos a actualizar
 * @returns {Promise<void>}
 */
async function commitUpdatesInBatches(db, updates) {
  for (let i = 0; i < updates.length; i += BATCH_MAX) {
    const chunk = updates.slice(i, i + BATCH_MAX);
    const batch = writeBatch(db);
    chunk.forEach(({ ref, data }) => batch.update(ref, data));
    await batch.commit();
  }
}

/**
 * Publica un cierre ya calculado: payments + expensesapartments visibles a residentes,
 * marca amenity charges como incluidos en cierre, registra aprobación en montlyaccount.
 *
 * @param {import("firebase/firestore").Firestore} db - Instancia de Firestore
 * @param {object} options - Opciones de aprobación
 * @param {string} options.cierreId - ID del documento montlyaccount a aprobar
 * @param {string} [options.approverEmail] - Email del usuario que aprueba
 * @param {string} [options.approverUid] - UID de Firebase Auth del aprobador
 * @returns {Promise<void>}
 * @throws {Error} Si el cierre no existe, no está en estado válido o ya fue aprobado
 */
export async function approveMontlyaccountCierre(db, { cierreId, approverEmail, approverUid }) {
  const cierreRef = doc(db, "montlyaccount", cierreId);
  const cierreSnap = await getDoc(cierreRef);
  if (!cierreSnap.exists()) {
    throw new Error("No se encontró el cierre.");
  }
  const c = cierreSnap.data();
  if (c.state !== "ok") {
    throw new Error("El cierre no está en estado válido para aprobar.");
  }
  if (c.approvalStatus === "approved") {
    throw new Error("Este cierre ya fue aprobado.");
  }

  const month = c.month;
  const year = c.year;

  // Todas las consultas fijan el edificio del cierre (reglas 0D): además de que
  // la regla pueda probarse, un doc de OTRO edificio que apunte a este cierre
  // (montlyaccountId / lockedByCierreId adivinables) no entra al batch y no
  // traba la aprobación.
  const [paySnap, eaSnap, amenitySnap, amenityLockedSnap] = await Promise.all([
    getDocs(consultaDelCierre(db, "payments", cierreId, c.buildingid)),
    getDocs(consultaDelCierre(db, "expensesapartments", cierreId, c.buildingid)),
    getDocs(
      query(
        collection(db, "amenitycharges"),
        where("buildingid", "in", tolerantId(c.buildingid)),
        where("month", "==", month),
        where("year", "==", year),
        where("status", "==", "pending")
      )
    ),
    // Los amenities que ESTE cierre bloqueó en su creación (status locked_for_cierre):
    // la query de "pending" de arriba no los ve, y sin esto quedan pegados en
    // locked_for_cierre en vez de pasar a included_in_cierre al aprobar.
    getDocs(consultaDelCierre(db, "amenitycharges", cierreId, c.buildingid, "lockedByCierreId")),
  ]);

  const updates = [];

  paySnap.docs.forEach((d) => {
    updates.push({ ref: d.ref, data: { publishedToResidents: true } });
  });

  eaSnap.docs.forEach((d) => {
    updates.push({ ref: d.ref, data: { publishedToResidents: true } });
  });

  amenitySnap.docs.forEach((chargeDoc) => {
    const charge = chargeDoc.data();
    if ((Number(charge.cost) || 0) <= 0) return;
    updates.push({
      ref: chargeDoc.ref,
      data: {
        status: "included_in_cierre",
        cierreMonth: month,
        cierreYear: year,
      },
    });
  });

  amenityLockedSnap.docs.forEach((chargeDoc) => {
    const charge = chargeDoc.data();
    if ((Number(charge.cost) || 0) <= 0) return;
    if (charge.status !== "locked_for_cierre") return; // solo los bloqueados por este cierre
    updates.push({
      ref: chargeDoc.ref,
      data: {
        status: "included_in_cierre",
        cierreMonth: month,
        cierreYear: year,
      },
    });
  });

  await commitUpdatesInBatches(db, updates);

  await updateDoc(cierreRef, {
    approvalStatus: "approved",
    approvedAt: serverTimestamp(),
    approvedByEmail: approverEmail || null,
    approvedByUid: approverUid || null,
  });
}
